/**
 * Notifications service
 * Provides typed functions for interacting with notification tables
 */

import { supabase } from '../supabase';
import type {
  NotificationPreferences,
  NotificationPreferencesInsert,
  NotificationPreferencesUpdate,
  EventNotificationSubscription,
  EventNotificationSubscriptionInsert,
  EventNotificationSubscriptionUpdate,
  UserNotification,
} from '../types/notifications.types';
import { ReminderTimeOption } from '../types/notifications.types';

const USER_NOTIFICATION_PAGE_SIZE = 25;

// ============================================================================
// Manual Event Notifications (Edge Function)
// ============================================================================

interface SendEventNotificationRequest {
  eventId: number;
  customMessage?: string;
  isNewEvent?: boolean;
}

interface SendEventNotificationResponse {
  success: boolean;
  notificationId?: string;
  usersNotified?: number;
  message?: string;
  error?: string;
}

export interface PaginatedUserNotificationsResponse {
  data: UserNotification[];
  nextCursor: number | undefined;
}

export interface NotificationInboxState {
  hasNewNotifications: boolean;
  latestNotificationCreatedAt: string | null;
  notificationsLastSeenAt: string | null;
}

export type NotificationDestinationAvailabilityMap = Record<string, boolean>;

function requireUserId(userId: string | null | undefined): string {
  if (!userId) {
    throw new Error('User not authenticated');
  }

  return userId;
}

/**
 * Send a manual notification for an event (create or update)
 * Called when admin explicitly chooses to send notification via checkbox
 */
export async function sendEventNotification(
  eventId: number,
  options?: {
    customMessage?: string;
    isNewEvent?: boolean;
  }
): Promise<SendEventNotificationResponse> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('User must be authenticated to send notifications');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL is not configured');
  }

  const requestBody: SendEventNotificationRequest = {
    eventId,
    customMessage: options?.customMessage,
    isNewEvent: options?.isNewEvent ?? false,
  };

  try {
    const response = await fetch(
      `${supabaseUrl}/functions/v1/send-event-notification`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(requestBody),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || 'Failed to send notification',
      };
    }

    return {
      success: true,
      notificationId: data.notificationId,
      usersNotified: data.usersNotified,
      message: data.message,
    };
  } catch (error) {
    console.error('Error calling send-event-notification:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// ============================================================================
// Notification Preferences (Global)
// ============================================================================

/**
 * Get the current user's notification preferences
 */
export async function getNotificationPreferences(
  userId: string | null | undefined
): Promise<NotificationPreferences | null> {
  const resolvedUserId = requireUserId(userId);

  const { data, error } = await supabase
    .from('notification_preferences')
    .select('*')
    .eq('user_id', resolvedUserId)
    .maybeSingle();

  if (error) {
    throw new Error(
      `Failed to fetch notification preferences: ${error.message}`
    );
  }

  return data;
}

/**
 * Create or update notification preferences for the current user
 */
export async function upsertNotificationPreferences(
  userId: string | null | undefined,
  preferences: NotificationPreferencesUpdate
): Promise<NotificationPreferences> {
  const resolvedUserId = requireUserId(userId);

  const insertData: NotificationPreferencesInsert = {
    user_id: resolvedUserId,
    ...preferences,
  };

  const { data, error } = await supabase
    .from('notification_preferences')
    .upsert(insertData, { onConflict: 'user_id' })
    .select()
    .single();

  if (error) {
    throw new Error(
      `Failed to upsert notification preferences: ${error.message}`
    );
  }

  return data;
}

// ============================================================================
// User Notification Inbox
// ============================================================================

/**
 * Fetch a paginated page of user notifications
 */
export async function getUserNotifications(
  userId: string | null | undefined,
  pageParam: number = 0,
  pageSize: number = USER_NOTIFICATION_PAGE_SIZE
): Promise<PaginatedUserNotificationsResponse> {
  requireUserId(userId);

  const { data, error } = await supabase
    .from('user_notifications')
    .select('*')
    .order('created_at', { ascending: false })
    .range(pageParam, pageParam + pageSize - 1);

  if (error) {
    throw new Error(`Failed to fetch notifications: ${error.message}`);
  }

  const notifications = (data as UserNotification[]) ?? [];

  return {
    data: notifications,
    nextCursor:
      notifications.length === pageSize ? pageParam + pageSize : undefined,
  };
}

/**
 * Fetch a single notification by ID
 */
export async function getUserNotificationById(
  userId: string | null | undefined,
  id: string
): Promise<UserNotification | null> {
  requireUserId(userId);

  const { data, error } = await supabase
    .from('user_notifications')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch notification: ${error.message}`);
  }

  return (data as UserNotification | null) ?? null;
}

/**
 * Get the current unread notification count
 */
export async function getUnreadNotificationCount(
  userId: string | null | undefined
): Promise<number> {
  requireUserId(userId);

  const { count, error } = await supabase
    .from('user_notifications')
    .select('id', { count: 'exact', head: true })
    .is('read_at', null);

  if (error) {
    throw new Error(
      `Failed to fetch unread notification count: ${error.message}`
    );
  }

  return count ?? 0;
}

/**
 * Get bell-dot state for the inbox by comparing the latest notification
 * against the user's last inbox-open timestamp.
 */
export async function getNotificationInboxState(
  userId: string | null | undefined
): Promise<NotificationInboxState> {
  const resolvedUserId = requireUserId(userId);

  const [{ data: profile, error: profileError }, { data, error }] =
    await Promise.all([
      supabase
        .from('user_profiles')
        .select('notifications_last_seen_at')
        .eq('id', resolvedUserId)
        .single(),
      supabase
        .from('user_notifications')
        .select('created_at')
        .order('created_at', { ascending: false })
        .limit(1),
    ]);

  if (profileError) {
    throw new Error(
      `Failed to fetch inbox seen state: ${profileError.message}`
    );
  }

  if (error) {
    throw new Error(
      `Failed to fetch latest notification timestamp: ${error.message}`
    );
  }

  const notificationsLastSeenAt =
    (profile?.notifications_last_seen_at as string | null | undefined) ?? null;
  const latestNotificationCreatedAt =
    (data?.[0]?.created_at as string | undefined) ?? null;

  return {
    hasNewNotifications:
      !!latestNotificationCreatedAt &&
      (!notificationsLastSeenAt ||
        latestNotificationCreatedAt > notificationsLastSeenAt),
    latestNotificationCreatedAt,
    notificationsLastSeenAt,
  };
}

/**
 * Mark the inbox as seen to clear the bell dot without changing read state.
 */
export async function markNotificationsInboxSeen(
  userId: string | null | undefined
): Promise<void> {
  const resolvedUserId = requireUserId(userId);

  const { error } = await supabase
    .from('user_profiles')
    .update({ notifications_last_seen_at: new Date().toISOString() })
    .eq('id', resolvedUserId);

  if (error) {
    throw new Error(`Failed to mark inbox seen: ${error.message}`);
  }
}

/**
 * Mark a single notification as read
 */
export async function markUserNotificationRead(
  userId: string | null | undefined,
  id: string
): Promise<void> {
  requireUserId(userId);

  const { error } = await supabase
    .from('user_notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('id', id)
    .is('read_at', null);

  if (error) {
    throw new Error(`Failed to mark notification read: ${error.message}`);
  }
}

/**
 * Mark all notifications as read for the current user
 */
export async function markAllUserNotificationsRead(
  userId: string | null | undefined
): Promise<void> {
  requireUserId(userId);

  const { error } = await supabase
    .from('user_notifications')
    .update({ read_at: new Date().toISOString() })
    .is('read_at', null);

  if (error) {
    throw new Error(`Failed to mark notifications read: ${error.message}`);
  }
}

/**
 * Mark notifications as read using the shared delivery key from a push open
 */
export async function markNotificationsReadByDeliveryKey(
  userId: string | null | undefined,
  deliveryKey: string
): Promise<void> {
  requireUserId(userId);

  const { error } = await supabase
    .from('user_notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('delivery_key', deliveryKey)
    .is('read_at', null);

  if (error) {
    throw new Error(`Failed to mark notification read: ${error.message}`);
  }
}

/**
 * Check whether the notification's destination is still accessible
 */
export async function isNotificationDestinationAvailable(
  userId: string | null | undefined,
  notification: UserNotification
): Promise<boolean> {
  switch (notification.destination_kind) {
    case 'event': {
      if (!notification.event_id) {
        return false;
      }

      const { data, error } = await supabase
        .from('events')
        .select('id')
        .eq('id', notification.event_id)
        .maybeSingle();

      return !error && !!data;
    }
    case 'post': {
      if (!notification.post_id) {
        return false;
      }

      const { data, error } = await supabase
        .from('posts_with_author')
        .select('id')
        .eq('id', notification.post_id)
        .maybeSingle();

      return !error && !!data;
    }
    case 'admin_queue': {
      const resolvedUserId = requireUserId(userId);
      const { data, error } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', resolvedUserId)
        .single();

      return !error && data?.role === 'admin';
    }
    default: {
      const _exhaustiveCheck: never = notification.destination_kind;
      return _exhaustiveCheck;
    }
  }
}

/**
 * Resolve destination availability for a batch of notifications.
 * Used by the inbox feed so cards can decide whether they are clickable.
 */
export async function getNotificationDestinationAvailabilityMap(
  userId: string | null | undefined,
  notifications: UserNotification[]
): Promise<NotificationDestinationAvailabilityMap> {
  const resolvedUserId = requireUserId(userId);

  if (notifications.length === 0) {
    return {};
  }

  const eventIds = Array.from(
    new Set(
      notifications
        .filter((notification) => notification.destination_kind === 'event')
        .map((notification) => notification.event_id)
        .filter((eventId): eventId is number => eventId !== null)
    )
  );

  const postIds = Array.from(
    new Set(
      notifications
        .filter((notification) => notification.destination_kind === 'post')
        .map((notification) => notification.post_id)
        .filter((postId): postId is number => postId !== null)
    )
  );

  const hasAdminQueueNotifications = notifications.some(
    (notification) => notification.destination_kind === 'admin_queue'
  );

  const [availableEventIds, availablePostIds, canAccessAdminQueue] =
    await Promise.all([
      (async () => {
        if (eventIds.length === 0) {
          return new Set<number>();
        }

        const { data, error } = await supabase
          .from('events')
          .select('id')
          .in('id', eventIds);

        if (error) {
          throw new Error(
            `Failed to resolve event availability: ${error.message}`
          );
        }

        return new Set((data ?? []).map((row) => row.id as number));
      })(),
      (async () => {
        if (postIds.length === 0) {
          return new Set<number>();
        }

        const { data, error } = await supabase
          .from('posts_with_author')
          .select('id')
          .in('id', postIds);

        if (error) {
          throw new Error(
            `Failed to resolve post availability: ${error.message}`
          );
        }

        return new Set((data ?? []).map((row) => row.id as number));
      })(),
      (async () => {
        if (!hasAdminQueueNotifications) {
          return false;
        }

        const { data, error } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', resolvedUserId)
          .single();

        if (error) {
          throw new Error(
            `Failed to resolve admin queue availability: ${error.message}`
          );
        }

        return data.role === 'admin';
      })(),
    ]);

  return notifications.reduce<NotificationDestinationAvailabilityMap>(
    (availabilityMap, notification) => {
      switch (notification.destination_kind) {
        case 'event':
          availabilityMap[notification.id] =
            notification.event_id !== null &&
            availableEventIds.has(notification.event_id);
          return availabilityMap;
        case 'post':
          availabilityMap[notification.id] =
            notification.post_id !== null &&
            availablePostIds.has(notification.post_id);
          return availabilityMap;
        case 'admin_queue':
          availabilityMap[notification.id] = canAccessAdminQueue;
          return availabilityMap;
        default: {
          const _exhaustiveCheck: never = notification.destination_kind;
          return _exhaustiveCheck;
        }
      }
    },
    {}
  );
}

// ============================================================================
// Event Notification Subscriptions (Per-Event)
// ============================================================================

/**
 * Get the current user's subscription for a specific event
 */
export async function getEventSubscription(
  userId: string | null | undefined,
  eventId: number
): Promise<EventNotificationSubscription | null> {
  const resolvedUserId = requireUserId(userId);

  const { data, error } = await supabase
    .from('event_notification_subscriptions')
    .select('*')
    .eq('event_id', eventId)
    .eq('user_id', resolvedUserId)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch event subscription: ${error.message}`);
  }

  return data;
}

/**
 * Create or update an event subscription for the current user
 */
export async function upsertEventSubscription(
  userId: string | null | undefined,
  eventId: number,
  subscription: EventNotificationSubscriptionUpdate
): Promise<EventNotificationSubscription> {
  const resolvedUserId = requireUserId(userId);

  const insertData: EventNotificationSubscriptionInsert = {
    user_id: resolvedUserId,
    event_id: eventId,
    ...subscription,
  };

  const { data, error } = await supabase
    .from('event_notification_subscriptions')
    .upsert(insertData, { onConflict: 'user_id,event_id' })
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to upsert event subscription: ${error.message}`);
  }

  return data;
}

/**
 * Delete an event subscription for the current user
 */
export async function deleteEventSubscription(
  userId: string | null | undefined,
  eventId: number
): Promise<void> {
  const resolvedUserId = requireUserId(userId);

  const { error } = await supabase
    .from('event_notification_subscriptions')
    .delete()
    .eq('event_id', eventId)
    .eq('user_id', resolvedUserId);

  if (error) {
    throw new Error(`Failed to delete event subscription: ${error.message}`);
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert an array of selected ReminderTimeOptions to subscription flags
 */
export function selectedRemindersToSubscription(
  selectedReminders: ReminderTimeOption[]
): EventNotificationSubscriptionUpdate {
  return {
    remind_3_days: selectedReminders.includes(ReminderTimeOption.THREE_DAYS),
    remind_1_day: selectedReminders.includes(ReminderTimeOption.ONE_DAY),
    remind_1_hr: selectedReminders.includes(ReminderTimeOption.ONE_HOUR),
  };
}

/**
 * Convert subscription flags to an array of selected ReminderTimeOptions
 */
export function subscriptionToSelectedReminders(
  subscription: EventNotificationSubscription | null
): ReminderTimeOption[] {
  if (!subscription) {
    return [];
  }

  const selected: ReminderTimeOption[] = [];

  if (subscription.remind_3_days) {
    selected.push(ReminderTimeOption.THREE_DAYS);
  }
  if (subscription.remind_1_day) {
    selected.push(ReminderTimeOption.ONE_DAY);
  }
  if (subscription.remind_1_hr) {
    selected.push(ReminderTimeOption.ONE_HOUR);
  }

  return selected;
}
